"use client";

import * as React from "react";
import { StaticImageData } from "next/image";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import LinkWithLang, {
  type LinkRouteDefaults,
} from "@stadium/shared/src/components/link-with-lang";
import Multimedia from "@stadium/shared/src/components/shared/multimedia";

import { B_06, BANNER_DEFAULTS } from "@stadium/shared/src/helpers/plasmic/static-slices";
import { type LinkWithLangType } from "@stadium/shared/src/constants/stores/routes";

import styles from "./main.module.scss";

export interface B06Props {
  // Content
  eyebrow?: string;
  heading?: string;
  description?: string;

  // CTAs
  primaryButtonText?: string;
  primaryButtonLinkType?: string;
  primaryButtonOpenInNewTab?: boolean;
  primaryButtonOtherUrl?: string;
  primaryButtonCategorySlug?: string;
  secondaryButtonText?: string;
  secondaryButtonLinkType?: string;
  secondaryButtonOpenInNewTab?: boolean;
  secondaryButtonOtherUrl?: string;
  secondaryButtonCategorySlug?: string;

  mobileImg?: string | StaticImageData;
  desktopImg?: string | StaticImageData;
  imageAlt?: string;

  overlayOpacity?: number;
  className?: string;
  linkRouteDefaults?: LinkRouteDefaults;
}

const B06: React.FC<Readonly<B06Props>> = ({
  eyebrow = BANNER_DEFAULTS[B_06].eyebrow,
  heading = BANNER_DEFAULTS[B_06].heading,
  description = BANNER_DEFAULTS[B_06].description,
  primaryButtonText = BANNER_DEFAULTS[B_06].primaryButtonText,
  primaryButtonLinkType = BANNER_DEFAULTS[B_06].primaryButtonLinkType,
  primaryButtonOpenInNewTab = false,
  primaryButtonOtherUrl,
  primaryButtonCategorySlug,
  secondaryButtonText = BANNER_DEFAULTS[B_06].secondaryButtonText,
  secondaryButtonLinkType = BANNER_DEFAULTS[B_06].secondaryButtonLinkType,
  secondaryButtonOpenInNewTab = false,
  secondaryButtonOtherUrl,
  secondaryButtonCategorySlug,
  mobileImg = BANNER_DEFAULTS[B_06].mobileImg,
  desktopImg = BANNER_DEFAULTS[B_06].desktopImg,
  imageAlt = BANNER_DEFAULTS[B_06].imageAlt,
  overlayOpacity = BANNER_DEFAULTS[B_06].overlayOpacity,
  className,
  linkRouteDefaults,
}) => {
  const { isMobileDevice, isTabletDevice } = React.useContext(CurrentDeviceContext);

  return (
    <section className={`${styles.sectionWrapper} ${className}`}>
      <div className={styles.divWrapper}>
        <div className={styles.contentContainer}>
          <div className={styles.backgroundImageWrapper}>
            {isMobileDevice || isTabletDevice ? (
              <Multimedia
                type="image"
                src={mobileImg}
                alt={imageAlt}
                className={styles.mobileImage}
              />
            ) : (
              <Multimedia
                type="image"
                src={desktopImg}
                alt={imageAlt}
                className={styles.desktopImage}
              />
            )}
          </div>

          <div
            className={styles.overlay}
            style={{
              opacity: overlayOpacity,
            }}
          />

          <div className={styles.contentOverlay}>
            <div className={styles.contentInner}>
              <div className={styles.contentWidth}>
                {eyebrow && <p className={styles.eyebrow}>{eyebrow}</p>}

                <h2 className={styles.heading}>{heading}</h2>

                {description && <p className={styles.description}>{description}</p>}

                <div className={styles.buttonWrapper}>
                  {primaryButtonText && primaryButtonLinkType && (
                    <LinkWithLang
                      {...linkRouteDefaults}
                      type={primaryButtonLinkType as LinkWithLangType}
                      data={{
                        code: primaryButtonCategorySlug,
                        url: primaryButtonOtherUrl,
                      }}
                      target={primaryButtonOpenInNewTab ? "_blank" : "_self"}
                      showExternalLinkIcon={primaryButtonOpenInNewTab}
                      className={styles.lightBtn}
                    >
                      {primaryButtonText}
                    </LinkWithLang>
                  )}
                  {secondaryButtonText && secondaryButtonLinkType && (
                    <LinkWithLang
                      {...linkRouteDefaults}
                      type={secondaryButtonLinkType as LinkWithLangType}
                      data={{
                        code: secondaryButtonCategorySlug,
                        url: secondaryButtonOtherUrl,
                      }}
                      target={secondaryButtonOpenInNewTab ? "_blank" : "_self"}
                      showExternalLinkIcon={secondaryButtonOpenInNewTab}
                      className={styles.lightOutlineBtn}
                    >
                      {secondaryButtonText}
                    </LinkWithLang>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default B06;
